import React, { useState } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../supabaseClient';
import './AuthPages.css';

const HERO_BG = "url('/images/backgrounds/login-hero.jpg')";

export default function RegisterPage() {
  const [form, setForm]       = useState({ firstName: '', lastName: '', email: '', password: '', phone: '' });
  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const { t } = useLanguage();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');

  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null, general: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.firstName?.trim())                  errs.firstName = t('register.firstNameRequired');
    if (!form.lastName || form.lastName.trim().length < 2) errs.lastName  = t('register.lastNameRequired');
    if (!form.email.includes('@'))                errs.email     = t('register.validEmail');
    if (form.password.length < 6)                 errs.password  = t('register.passwordMin');
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setLoading(true);
    const result = await register(form);
    setLoading(false);

    if (!result.success) {
      setErrors({ general: result.error });
      return;
    }

    if (result.needsVerification) {
      navigate('/verify-email', { state: { email: form.email, firstName: form.firstName, redirect: redirectTo } });
      return;
    }

    if (redirectTo) navigate(redirectTo, { replace: true });
    else if (location.state?.from) navigate(location.state.from, { replace: true });
    else navigate('/');
  };

  async function handleGoogleSignIn() {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}/auth/callback` },
      });
      if (error) { setErrors({ general: error.message }); setLoading(false); }
    } catch (err) {
      setErrors({ general: err.message || 'Google sign-in failed' });
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      {/* ── Left: form ── */}
      <div className="auth-page__form-side">
        <div className="auth-page__form-container">
          <Link to="/">
            <img src="/images/logo-transparent.png" alt="CosmoEstates" className="auth-page__logo-img" />
          </Link>

          <h1 className="auth-page__title">{t('register.createAccount')}</h1>
          <p className="auth-page__sub">{t('register.sub')}</p>

          {/* OAuth buttons */}
          <div className="auth-page__oauth">
            <button type="button" className="auth-page__oauth-btn" onClick={handleGoogleSignIn} disabled={loading} aria-label="Continue with Google">
              <svg width="20" height="20" viewBox="0 0 48 48">
                <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
                <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0124 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 01-4.087 5.571l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
              </svg>
              <span>{t('register.continueWithGoogle')}</span>
            </button>

          </div>

          <div className="auth-page__divider"><span>{t('register.orSignUpEmail')}</span></div>

          {errors.general && <div className="auth-page__error">{errors.general}</div>}

          <form onSubmit={handleSubmit} className="auth-page__form">
            <div className="auth-page__row">
              <div className="auth-field">
                <label>{t('register.firstName')}</label>
                <input
                  placeholder="Andreas"
                  value={form.firstName}
                  onChange={e => update('firstName', e.target.value)}
                  className={errors.firstName ? 'error' : ''}
                  autoComplete="given-name"
                  required
                />
                {errors.firstName && <span className="auth-field__err">{errors.firstName}</span>}
              </div>
              <div className="auth-field">
                <label>{t('register.lastName')}</label>
                <input
                  placeholder="Georgiou"
                  value={form.lastName}
                  onChange={e => update('lastName', e.target.value)}
                  className={errors.lastName ? 'error' : ''}
                  autoComplete="family-name"
                  required
                  minLength={2}
                />
                {errors.lastName && <span className="auth-field__err">{errors.lastName}</span>}
              </div>
            </div>

            <div className="auth-field">
              <label>{t('register.emailAddress')}</label>
              <input
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={e => update('email', e.target.value)}
                className={errors.email ? 'error' : ''}
                autoComplete="email"
              />
              {errors.email && <span className="auth-field__err">{errors.email}</span>}
            </div>

            <div className="auth-field">
              <label>{t('register.phoneNumber')} <span style={{ color: '#aaa', fontWeight: 400 }}>{t('register.phoneOptional')}</span></label>
              <input
                type="tel"
                placeholder="+357 99 000 000"
                value={form.phone}
                onChange={e => update('phone', e.target.value)}
                autoComplete="tel"
              />
            </div>

            <div className="auth-field">
              <label>{t('register.password')}</label>
              <input
                type="password"
                placeholder={t('register.passwordPlaceholder')}
                value={form.password}
                onChange={e => update('password', e.target.value)}
                className={errors.password ? 'error' : ''}
                autoComplete="new-password"
              />
              {errors.password && <span className="auth-field__err">{errors.password}</span>}
            </div>

            <button
              type="submit"
              className="auth-page__submit"
              disabled={
                loading ||
                !form.firstName?.trim() ||
                !form.lastName || form.lastName.trim().length < 2 ||
                !form.email.includes('@') ||
                form.password.length < 6
              }
            >
              {loading
                ? <span className="auth-page__spinner" />
                : t('register.createAccountBtn')
              }
            </button>
          </form>

          <div className="auth-page__switch">
            {t('register.alreadyHaveAccount')}{' '}
            <Link to={redirectTo ? `/login?redirect=${encodeURIComponent(redirectTo)}` : '/login'} state={{ from: location.state?.from }}>{t('register.signIn')}</Link>
          </div>
        </div>
      </div>

      {/* ── Right: Cyprus hero image ── */}
      <div className="auth-page__hero" style={{ backgroundImage: HERO_BG }}>
        <div className="auth-page__hero-overlay" />
        <div className="auth-page__hero-content">
          <h2 className="auth-page__hero-title">
            {t('register.heroTitleA')}<br />
            {t('register.heroTitleB')}<br />
            <em>{t('register.heroTitleC')}</em>
          </h2>
          <p className="auth-page__hero-subtitle">
            {t('register.heroSubtitle')}
          </p>
        </div>
      </div>
    </div>
  );
}
